import {FC, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useFlags} from "launchdarkly-react-client-sdk";
import {Badge} from "@interstate/components/Badge";
import {Card, CardContent, CardHeader} from "@interstate/components/Card";
import {useInterstateToken} from "@interstate/components/InterstateThemeProvider";
import {
  SimpleTable,
  TableBody,
  TableCell,
  TableRow
} from "@interstate/components/SimpleTable";
import {Tooltip} from "@interstate/components/Tooltip";
import {BadgeBar, BadgeMetadata} from "../../../../../badges";
import {useNavigation} from "../../../../../navigation";
import {EntitySourceType} from "../../../../EntitySourceType";
import {CreatePlan, CreateResult} from "../../view-model";
import {resultBadgeMetadata} from "./resultBadgeMetadata";
import {features} from "../../../../../features";
import "./OrganizationDetailsCard.scss";

export interface OrganizationDetailsCardProps {
  result: CreateResult;
  plan: CreatePlan;
}

export const OrganizationDetailsCard: FC<OrganizationDetailsCardProps> = ({
  result,
  plan
}) => {
  const {t} = useTranslation();
  const {navigateToEntity} = useNavigation();
  const token = useInterstateToken();
  const flags = useFlags();

  // Get the ultimate parent name for tenant display
  const ultimateParent = plan.source.hierarchy;
  const tenantName = ultimateParent?.custName ?? "";

  const {labelKey, variant, startIcon, endIcon} = useMemo<BadgeMetadata>(
    () => resultBadgeMetadata(token, result),
    [result, token]
  );

  const badge = (
    <Badge
      id={`result-badge-${result.caId}--${result.status}`}
      data-testid={`result-badge-${result.caId}--${result.status}`}
      className={`result-badge result-badge--${result.status}`}
      variant={variant}
      startIcon={startIcon}
      endIcon={endIcon}>
      <span className={"result-badge-label"}>{t(labelKey!)}</span>
    </Badge>
  );

  return (
    <Card
      className={"organization-details-card"}
      header={
        <CardHeader
          title={t("create-organization.steps.results.organization-details")}
          action={
            <BadgeBar>
              <div
                onClick={event => {
                  if (result.status === "created") {
                    event.stopPropagation();
                    navigateToEntity(
                      result.oeId!,
                      EntitySourceType.OPERATIONAL
                    );
                  }
                }}>
                {result.message ? (
                  <Tooltip
                    position={"left"}
                    size={"small"}
                    toolTipContent={result.message}>
                    {badge}
                  </Tooltip>
                ) : (
                  badge
                )}
              </div>
            </BadgeBar>
          }
        />
      }
      content={
        <CardContent>
          <SimpleTable
            id={"organization-details-table"}
            data-testid={"organization-details-table"}
            dataDensity={"small"}
            className={"organization-details-table"}
            background={"white"}
            bordered={false}
            hover={false}
            striped={false}>
            <TableBody>
              <TableRow>
                <TableCell className={"information-field"}>
                  {t("entity-detail.dealer.legal-name")}
                </TableCell>
                <TableCell
                  className={"information-value"}
                  id={"dealer-information-legal-name"}>
                  {plan.newEntity.organizationInformation.legalName}
                </TableCell>
              </TableRow>
              {flags[features.dev.createOrg.createWithTenant] && tenantName && (
                <TableRow>
                  <TableCell className={"information-field"}>
                    {t("entity-detail.dealer.tenant-name")}
                  </TableCell>
                  <TableCell
                    id={"dealer-information-tenant-name"}
                    className={"information-value"}>
                    {tenantName}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className={"information-field"}>
                  {t("entity-detail.dealer.dba-name")}
                </TableCell>
                <TableCell
                  id={"dealer-information-dba-name"}
                  className={"information-value"}>
                  <div>{plan.newEntity.organizationInformation.dbaName}</div>
                </TableCell>
              </TableRow>
            </TableBody>
          </SimpleTable>
        </CardContent>
      }
    />
  );
};
